import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, message, Spin } from 'antd';
import { logInstance } from '../../AxiosConfig';

const EditOtpModal = ({ cardId, onSuccess, onCancel, open }) => {
  const [step, setStep] = useState('request'); // 'request' | 'otp' | 'done'
  const [phone, setPhone] = useState(''); // masked phone for display
  const [fullPhone, setFullPhone] = useState(''); // full phone for verification
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [attempts, setAttempts] = useState(0);
  const [sessionData, setSessionData] = useState(null);

  // Timer effect
  useEffect(() => {
    if (step !== 'otp' || timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRequestOTP = async () => {
    try {
      setLoading(true);
      const response = await logInstance.post('/card/request-edit-otp', {
        cardId
      });
      setPhone(response.data.phone); // masked phone (6204422****)
      setFullPhone(response.data.fullPhone); // full phone for verification
      setStep('otp');
      setTimeLeft(300);
      setOtp('');
      setAttempts(0);
      message.success('OTP sent to your phone');
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      message.error('Please enter 6-digit OTP');
      return;
    }

    try {
      setLoading(true);
      const response = await logInstance.post('/card/verify-edit-otp', {
        cardId,
        otp,
        phone: fullPhone // Use full phone number
      });

      setSessionData(response.data);
      setStep('done');
      message.success('OTP verified successfully!');

      // Call success callback after short delay
      setTimeout(() => {
        onSuccess(response.data);
      }, 500);
    } catch (error) {
      const attemptsRemaining = error?.response?.data?.attemptsRemaining;
      setAttempts(prev => prev + 1);

      if (attemptsRemaining !== undefined) {
        message.error(`Invalid OTP. ${attemptsRemaining} attempts remaining`);
      } else {
        message.error(error?.response?.data?.message || 'Failed to verify OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtp('');
    setAttempts(0);
    handleRequestOTP();
  };

  return (
    <Modal
      title="Edit Profile Link"
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={400}
    >
      <Spin spinning={loading}>
        {step === 'request' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: '#6B7480', marginBottom: '24px' }}>
              We'll send an OTP to your registered phone number to verify edit access.
            </p>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleRequestOTP}
              loading={loading}
              style={{ height: '44px', borderRadius: '8px' }}
            >
              Send OTP
            </Button>
          </div>
        )}

        {step === 'otp' && (
          <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <p style={{ color: '#6B7480', marginBottom: '8px' }}>
                OTP sent to {phone}
              </p>
              <p style={{ color: '#E5484D', fontSize: '12px' }}>
                Expires in {formatTime(timeLeft)}
              </p>
            </div>

            <Input
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.slice(0, 6))}
              maxLength={6}
              type="number"
              size="large"
              style={{ marginBottom: '16px' }}
            />

            <Button
              type="primary"
              size="large"
              block
              onClick={handleVerifyOTP}
              loading={loading}
              disabled={otp.length !== 6}
              style={{ height: '44px', borderRadius: '8px', marginBottom: '12px' }}
            >
              Verify OTP
            </Button>

            <Button
              type="text"
              size="large"
              block
              onClick={handleResendOTP}
              style={{ color: '#1467E0' }}
            >
              Resend OTP
            </Button>
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: '#26a17b', marginBottom: '12px' }}>
              ✓ OTP Verified
            </p>
            <p style={{ color: '#6B7480', fontSize: '12px' }}>
              Your edit link is ready. Close this modal to see it.
            </p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default EditOtpModal;
